import React, { useContext, useEffect, useState } from 'react'

import moment from 'moment'

import { Box, Button, Flex } from '@driftt/dds'
import { Avatar, Div, H1, H5, Input } from '@driftt/tide-core'

import { fetchMissedAccountsCSV } from 'api/AccountActivityAPI'
import { driftErrorStateBuilder, driftLoadingStateBuilder } from 'components/States'
import { CurrentUserContext } from 'context/currentUserContext'
import { segmentTrack, TrackType } from 'utils/tracking'

import { fetchMissedAccountsReport } from './api'

import styles from './styles.module.css'

interface MissedAccount {
  accountId: string
  companyData: {
    name: string
    url: string
    logo: string
  }
  accountData: {
    name: string
  }
  score: number
  count: number
}

interface MissedAccountsRow {
  userInfo: {
    Id: string
    Email: string
  }
  driftAccountCount: number
  scoredAccountSamples: MissedAccount[]
}

interface MissedAccountsReport {
  orgName: string
  driftUserCount: number
  salesforceUserCount: number
  missingUserCount: number
  missedAccountsByUser: MissedAccountsRow[]
}

const formatPercent = (ratio: number) => {
  return (100 * ratio).toFixed(1)
}

const getUserAccountDescription = (userMissedAccounts: MissedAccountsRow) => {
  const accountNames = userMissedAccounts.scoredAccountSamples
    .map(a => a.accountData.name)
    .join(', ')
  const otherAccountCount =
    userMissedAccounts.driftAccountCount - userMissedAccounts.scoredAccountSamples.length
  return `Missed ${accountNames}${
    otherAccountCount > 0 ? ` + ${otherAccountCount} other accounts` : ''
  }`
}

const OrgMissedAccountsReport: React.FC<{ report: MissedAccountsReport; orgId?: number }> = ({
  report,
  orgId
}: {
  report: MissedAccountsReport
  orgId?: number
}) => {
  const totalUserCount = report.driftUserCount + report.missingUserCount
  const numUsersWithMissedAccounts = report.missedAccountsByUser.length
  const numOwnedMissedAccounts = report.missedAccountsByUser
    .map(missed => missed.driftAccountCount)
    .reduce((previous, current) => previous + current, 0)

  const handleDownloadReport = async () => {
    if (!orgId) {
      return
    }

    const downloadEl = document.createElement('a')
    try {
      const data = await fetchMissedAccountsCSV(orgId)
      const file = new Blob([data], {
        type: 'csv'
      })
      downloadEl.href = URL.createObjectURL(file)
      downloadEl.download = `${moment().format('DD/MM/YYYY')}-missed-accounts.csv`
      document.body.appendChild(downloadEl)
      downloadEl.click()
    } catch (e) {
      alert('Oops, something when wrong when exporting your report')
    }
  }
  return (
    <Div>
      <Div className={styles.card}>
        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            <H1>{`🎯 ${report?.orgName}'s Missed Accounts`}</H1>
            <p>
              {report?.orgName} has missed a total of <b>{numOwnedMissedAccounts} accounts</b> with
              owners in Salesforce
            </p>
          </Box>
          <Button onClick={handleDownloadReport}>Download CSV</Button>
        </Flex>
      </Div>
      <Div className={styles.card}>
        <Div className={styles.userStats}>
          <p>
            <span className={styles.userStatDescription}>Drift users</span>
            <br />
            {`${report.driftUserCount} (${formatPercent(report.driftUserCount / totalUserCount)}%)`}
          </p>
          <p>
            <span className={styles.userStatDescription}>Total missing users</span>
            <br />
            {`${report.missingUserCount} (${formatPercent(
              report.missingUserCount / totalUserCount
            )}%)`}
          </p>
          <p>
            <span className={styles.userStatDescription}>Salesforce users w/ missed accounts</span>
            <br />
            {`${numUsersWithMissedAccounts} (${formatPercent(
              numUsersWithMissedAccounts / totalUserCount
            )}%)`}
          </p>
        </Div>
      </Div>
      <Div className={styles.card}>
        {report.missedAccountsByUser.map(userMissedAccounts => (
          <Div key={userMissedAccounts.userInfo.Id} className={styles.missedAccountsRow}>
            <Div className={styles.missedAccountsLogos}>
              {userMissedAccounts.scoredAccountSamples.map((sample, index) => (
                <Avatar
                  type="company"
                  key={sample.companyData.name || `avatar-${index}`}
                  company={sample.companyData}
                />
              ))}
            </Div>
            <Div>
              <b>{userMissedAccounts.userInfo.Email}</b>
              <br />
              {getUserAccountDescription(userMissedAccounts)}
            </Div>
          </Div>
        ))}
      </Div>
    </Div>
  )
}

const MissedAccountsDemoPage: React.FC<{}> = () => {
  const [orgId, setOrgId] = useState<number>()
  const [loading, setLoading] = useState<boolean>()
  const [report, setReport] = useState<MissedAccountsReport>()

  const currentUser = useContext(CurrentUserContext)

  useEffect(() => {
    if (loading && orgId) {
      segmentTrack(TrackType.LOADED_MISSED_ACCOUNTS_REPORT, { orgId })
      fetchMissedAccountsReport(orgId)
        .then(data => setReport(data))
        .finally(() => setLoading(false))
    }
  }, [loading, orgId])

  const ErrorComponent = driftErrorStateBuilder("Can't load report")
  const LoadingComponent = driftLoadingStateBuilder(
    'Building report. This may take a couple of minutes...'
  )

  if (currentUser?.orgId !== 1) {
    return <ErrorComponent />
  }

  return (
    <Div className={styles.DemoPage}>
      {!report && (
        <Div className={styles.Header}>
          <H5>Shows accounts missed for Salesforce users without Drift seats</H5>
          <Div className={styles.inputControls}>
            <Input
              className={styles.orgIdInput}
              onChange={(e: any) => setOrgId(e.target.value)}
              placeholder="Enter your customer's org id here"
            />
            <Button onClick={() => orgId && orgId > 0 && setLoading(true)}>Run</Button>
          </Div>
        </Div>
      )}
      {loading && <LoadingComponent />}
      {report && <OrgMissedAccountsReport orgId={orgId} report={report} />}
    </Div>
  )
}

export default MissedAccountsDemoPage
